import { makeFilterItemOptionsFromSuggestionField } from "components/Filters/helpers";
import { FiltersItemsOptionsMap } from "components/Filters/types";
import { SearchSuggestionsField } from "types/generated";

import {
  FILTER_KEY_CATEGORIES,
  FILTER_KEY_CONFIGURED,
  INTEGRATION_FILTER_CONFIGURED,
  INTEGRATION_FILTER_TYPE,
  INTEGRATIONS_FILTERS_DICTIONARY,
} from "./constants";
import { getIntegrationById, integrationIds } from "./registry";
import { IntegrationId } from "./registry/constants";
import { IntegrationType, IntegrationView } from "./types";

export const filterIntegrations = (
  integrations: (IntegrationView | undefined)[],
  searchQuery: string,
  categoryValues: string[] = [],
  configuredValues: boolean[] = []
): IntegrationView[] => {
  const filterIntegrationsBySearchQuery = (
    integration: IntegrationView | undefined
  ): integration is IntegrationView => {
    if (!integration) return false;

    if (searchQuery && !integration.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    const filteredByCategory =
      !categoryValues.length ||
      categoryValues.some((value) => INTEGRATIONS_FILTERS_DICTIONARY[integration.type] === value);

    if (!filteredByCategory) return false;

    const isIntegrationConfigured = Boolean(integration.count && integration.count > 0);

    if (configuredValues.length && !configuredValues.includes(isIntegrationConfigured)) {
      return false;
    }

    return true;
  };
  const filteredIntegrations = integrations.filter(filterIntegrationsBySearchQuery);

  return filteredIntegrations;
};

export const createIntegrationsFiltersOptionsMap: (
  integrations: IntegrationView[],
  showConfiguredFilter?: boolean,
  blobStorageEnabled?: boolean,
  originEnabled?: boolean
) => FiltersItemsOptionsMap = (
  integrations,
  showConfiguredFilter = false,
  blobStorageEnabled = false,
  originEnabled = false
) => {
  const filtersItemsOptionsMap: FiltersItemsOptionsMap = new Map([]);

  // We don't have an API for SearchSuggestionsField, so we mock the object in the FE.
  const cloneCategoriesFiltersItem: SearchSuggestionsField = JSON.parse(
    JSON.stringify(INTEGRATION_FILTER_TYPE)
  );
  const cloneConfiguredFiltersItem: SearchSuggestionsField = JSON.parse(
    JSON.stringify(INTEGRATION_FILTER_CONFIGURED)
  );

  integrationIds.forEach((id) => {
    // TODO: remove this check together with the vcs-provider-blob-storage feature flag.
    if (id === IntegrationId.BLOB_STORAGE && !blobStorageEnabled) return;
    if (id === IntegrationId.ORIGIN && !originEnabled) return;

    const integration = getIntegrationById(id);
    if (!integration) return;
    if (!cloneCategoriesFiltersItem.possibleValuesEnum) return;

    const ind = cloneCategoriesFiltersItem.possibleValuesEnum.values.indexOf(integration.type);
    cloneCategoriesFiltersItem.possibleValuesEnum.counts[ind] += 1;
  });

  // populate the configured count
  integrations.forEach((integration) => {
    if (!cloneConfiguredFiltersItem.possibleValuesBoolean) return;

    if (integration.count && integration.count > 0) {
      cloneConfiguredFiltersItem.possibleValuesBoolean.counts[0] += 1;
    } else {
      cloneConfiguredFiltersItem.possibleValuesBoolean.counts[1] += 1;
    }
  });

  const categoriesOptions = makeFilterItemOptionsFromSuggestionField(cloneCategoriesFiltersItem);
  const configuredOptions = makeFilterItemOptionsFromSuggestionField(cloneConfiguredFiltersItem);

  if (categoriesOptions) {
    const mappedFilterOptions = categoriesOptions.map((option) => ({
      ...option,
      value: INTEGRATIONS_FILTERS_DICTIONARY[option.value as IntegrationType] ?? option.value,
    }));
    filtersItemsOptionsMap.set(FILTER_KEY_CATEGORIES, mappedFilterOptions);
  }
  if (configuredOptions && showConfiguredFilter) {
    filtersItemsOptionsMap.set(FILTER_KEY_CONFIGURED, configuredOptions);
  }

  return filtersItemsOptionsMap;
};
